import { z } from 'zod';
import { NOTE_ID_RE } from './ids.js';
import { InputLinkSchema } from './todos.js';
// ---------------------------------------------------------------------------
// Scratchpad notes — event-sourced. Ported from the daemon's schema.ts /
// note-store.ts. Two kinds share one capture surface + one event log:
//  - `research`: agent goes and finds things out, produces an artifact.
//  - `guidance`: standing steering context read as extra context by runs.
// Privacy invariant (load-bearing): guidance is ALWAYS private; raw note text
// never enters git — only published research artifacts do.
// ---------------------------------------------------------------------------
export const NOTE_KINDS = ['research', 'guidance'];
export const NOTE_STATUSES = [
    'idea',
    'active',
    'queued',
    'researching',
    'ready',
    'failed',
    'done',
    'archived',
];
// Derived research lifecycle for a research note (null for guidance and archived
// notes, which are outside the queue). The API
// computes it from the claim, lease, attempt count, and retry time.
export const RESEARCH_STATES = [
    'queued',
    'running',
    'retry_scheduled',
    'failed',
    'completed',
];
/** Claims after this many attempts fail for good until the request is requeued. */
export const RESEARCH_MAX_ATTEMPTS = 3;
export const GUIDANCE_SCOPES = ['daily_digest', 'intraday', 'research', 'weekly_brief'];
export const NOTE_RESEARCH_MODES = ['web', 'quick'];
export const NOTE_EVENT_ACTIONS = [
    'create',
    'edit',
    'set_status',
    'set_privacy', // toggle a note public/private (research only; guidance is always private)
    'request_research', // ask for (or re-ask for) research on this note
    'research_started', // scheduled task began a pass
    'research_completed', // scheduled task produced an artifact
    'research_failed', // scheduled task could not complete
    'comment', // operator asks a follow-up on a (usually researched) note; auto-queues
    'answer', // the research sweep answers a comment in-thread (never rewrites the poster body)
    'reorder', // manual ordering — payload.order lists note_ids in display order
    'delete', // soft delete (hidden from the list, kept in the log)
    'restore', // un-delete
];
// Where a note came from. `manual` = typed into the Scratchpad. `slack` = the
// research intake pass ingested a message from a configured drop channel.
export const NOTE_ORIGINS = ['manual', 'slack', 'assistant'];
export const NoteSourceSchema = z
    .object({
    origin: z.enum(NOTE_ORIGINS),
    channel_id: z.string().optional(),
    channel: z.string().optional(),
    channel_name: z.string().optional(),
    ts: z.string().optional(),
    permalink: z.string().optional(),
    author_user_id: z.string().optional(),
    author: z.string().optional(),
})
    .strict();
export const NoteEventSchema = z
    .object({
    event_id: z.string().min(1),
    created_at: z.string().min(1),
    note_id: z.string().regex(NOTE_ID_RE),
    action: z.enum(NOTE_EVENT_ACTIONS),
    payload: z
        .object({
        text: z.string().optional(),
        title: z.string().optional(),
        kind: z.enum(NOTE_KINDS).optional(), // set on create; default 'research'
        status: z.enum(NOTE_STATUSES).optional(),
        mode: z.enum(NOTE_RESEARCH_MODES).optional(),
        artifact_href: z.string().optional(),
        artifact_path: z.string().optional(),
        summary: z.string().optional(),
        error: z.string().optional(),
        source: NoteSourceSchema.optional(),
        private: z.boolean().optional(), // create/set_privacy: keep out of the shared library
        order: z.array(z.string()).optional(), // reorder: note_ids in display order
        comment_id: z.string().optional(), // comment: minted id; answer: which comment it answers
        author: z.string().optional(), // comment/answer author (operator name, or "assistant")
        // research_started/completed/failed: which claim, which attempt, and how it ended
        claim_id: z.string().nullable().optional(),
        attempt: z.number().int().positive().optional(),
        reclaimed: z.boolean().optional(), // started by taking over an expired claim
        recovered: z.boolean().optional(), // completed from an abandoned run's published artifact
        final: z.boolean().optional(), // failure used the last attempt
    })
        .strict()
        .default({}),
})
    .strict();
// A follow-up thread entry on a note: the operator's question and, once the
// research sweep responds, its in-thread answer. Answers append — never rewrite.
export const NoteCommentSchema = z
    .object({
    comment_id: z.string(),
    text: z.string(),
    author: z.string().nullable(),
    created_at: z.string(),
    answered: z.boolean(),
    answer: z
        .object({ text: z.string(), author: z.string().nullable(), created_at: z.string() })
        .nullable(),
})
    .strict();
// Projected research state on a note (null until research is requested).
export const NoteResearchSchema = z
    .object({
    status: z.enum(['requested', 'running', 'ready', 'failed']),
    mode: z.enum(NOTE_RESEARCH_MODES),
    requested_at: z.string(),
    updated_at: z.string(),
    artifact_href: z.string().nullable(),
    title: z.string().nullable(),
    summary: z.string().nullable(),
    error: z.string().nullable(),
})
    .strict();
export const SLACK_REPLY_STATUSES = ['pending', 'delivered', 'failed', 'not_applicable'];
export const NoteSchema = z
    .object({
    note_id: z.string().regex(NOTE_ID_RE),
    kind: z.enum(NOTE_KINDS),
    title: z.string().nullable(),
    text: z.string(),
    status: z.enum(NOTE_STATUSES),
    // guidance is ALWAYS private; research is public by default (its completed
    // write-up is published to the shared library + committed to git) unless
    // flagged private. The raw note text/log never enters git.
    private: z.boolean(),
    source: NoteSourceSchema.nullable(),
    scopes: z.array(z.enum(GUIDANCE_SCOPES)),
    links: z.array(InputLinkSchema),
    research_requested: z.boolean(),
    research: NoteResearchSchema.nullable(),
    research_claimed_at: z.string().nullable(),
    research_claimed_by: z.string().nullable(),
    research_attempts: z.number().int().nonnegative(),
    research_next_attempt_at: z.string().nullable(),
    research_lease_expires_at: z.string().nullable(),
    research_state: z.enum(RESEARCH_STATES).nullable(),
    slack_reply_at: z.string().nullable(),
    slack_reply_ts: z.string().nullable(),
    slack_reply_status: z.enum(SLACK_REPLY_STATUSES),
    slack_reply_attempts: z.number().int().nonnegative(),
    slack_reply_error: z.string().nullable(),
    comments: z.array(NoteCommentSchema),
    follow_up_requested: z.boolean(),
    order: z.number().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    deleted: z.boolean(),
})
    .strict();
export const CreateGuidanceSchema = z
    .object({
    kind: z.literal('guidance'),
    title: z.string().trim().min(1).max(240),
    text: z.string().min(1).max(40_000),
    scopes: z
        .array(z.enum(GUIDANCE_SCOPES))
        .min(1)
        .default([...GUIDANCE_SCOPES]),
    source: NoteSourceSchema.optional(),
})
    .strict();
export const CreateResearchRequestSchema = z
    .object({
    kind: z.literal('research'),
    title: z.string().trim().min(1).max(240),
    text: z.string().min(1).max(40_000),
    links: z.array(InputLinkSchema).max(20).default([]),
    mode: z.enum(NOTE_RESEARCH_MODES).default('web'),
    source: NoteSourceSchema.optional(),
})
    .strict();
export const CreateNoteInputSchema = z.discriminatedUnion('kind', [
    CreateGuidanceSchema,
    CreateResearchRequestSchema,
]);
export const UpdateNoteInputSchema = z
    .object({
    title: z.string().trim().min(1).max(240).optional(),
    text: z.string().min(1).max(40_000).optional(),
    scopes: z.array(z.enum(GUIDANCE_SCOPES)).min(1).optional(),
    links: z.array(InputLinkSchema).max(20).optional(),
    status: z.enum(NOTE_STATUSES).optional(),
})
    .strict();
export const SWEEP_KINDS = ['daily_digest', 'intraday', 'research', 'weekly_brief'];
